import React, { forwardRef, useCallback, useEffect, useImperativeHandle } from 'react';

import useGalleryState from '@rover/react-lib/src/components/ImageGallery/hooks/useGalleryState';
import ImageGallery from '@rover/react-lib/src/components/ImageGallery/ImageGallery';
import type {
  GalleryItem,
  PaginationConfig,
} from '@rover/react-lib/src/components/ImageGallery/ImageGallery.common';
import type { IntroVideoGalleryHandlers } from '@rover/react-lib/src/components/IntroVideo/useIntroVideoTracking';

export type SitterProfileGalleryHandle = {
  openLightbox: (index: number) => void;
  openGrid: () => void;
};

type Props = Partial<IntroVideoGalleryHandlers> & {
  images: GalleryItem[] | undefined;
  pagination?: PaginationConfig;
  renderImageOverlay?: (index: number) => React.ReactNode;
  onViewGallery?: () => void;
  onOpenChange?: (isOpen: boolean) => void;
  autoPlayVideos?: boolean;
};

const SitterProfileGallery = forwardRef<SitterProfileGalleryHandle, Props>(
  (
    { images, pagination, renderImageOverlay, onViewGallery, onOpenChange, ...introVideoProps },
    ref
  ): JSX.Element | null => {
    const { isOpen, openLightbox, openGrid, galleryProps } = useGalleryState();
    const { onViewGallery: showGrid } = galleryProps;

    useEffect(() => {
      onOpenChange?.(isOpen);
    }, [isOpen, onOpenChange]);

    useImperativeHandle(ref, () => ({ openLightbox, openGrid }), [openLightbox, openGrid]);

    const handleViewGallery = useCallback((): void => {
      showGrid?.();
      onViewGallery?.();
    }, [showGrid, onViewGallery]);

    if (!isOpen) return null;

    return (
      <ImageGallery
        images={images}
        pagination={pagination}
        renderImageOverlay={renderImageOverlay}
        {...galleryProps}
        onViewGallery={handleViewGallery}
        {...introVideoProps}
      />
    );
  }
);

SitterProfileGallery.displayName = 'SitterProfileGallery';

export default SitterProfileGallery;
